import React, { FunctionComponent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  SelectionChangedEvent, ColDef, ColGroupDef, GridApi, RowNode, ColumnApi
} from '@ag-grid-enterprise/all-modules';
import CommonGrid from 'shared/commonGrid';
import { ROWSELECTION } from 'shared/commonGrid/grid-enums';
import ActionMenuCellRenderer from 'shared/commonGrid/action-menu-cell-renderer';
import { AccessFunctionGridRowData } from 'data/accessFunction/accessFunction.types';
import { GridSettings } from 'data/types';
import ModalDialog from 'shared/modal/dialog';
import useConfirmActionDialog from 'utils/hooks/useConfirmActionDialog';
import NotApplicableCellRenderer from 'components/NotApplicableCellRenderer';
import { AccessFunctionMenuActions } from 'routes/AccessFunction/AccessFunction.types';
import AccessFunctionStatusCellRenderer from 'routes/AccessFunction/components/AccessFunctionStatusCellRenderer';
import AccessFunctionDestinationCellRenderer from 'routes/AccessFunction/components/AccessFunctionDestinationCellRenderer';
import AccessFunctionConfiguredStatusCellRenderer from 'routes/AccessFunction/components/AccessFunctionConfiguredStatusCellRenderer';
import AccessFunctionActionMenu from '../AccessFunctionActionMenu';
import './access-function-list.scss';
import ExportReportDialogBody, { ExportOption, ExportReportDialogBodyFormRef } from '../DialogBody/ExportReportDialogBody';

interface Props {
  data: AccessFunctionGridRowData[]
  hasPrivilegesToCreate: boolean
  hasPrivilegeToDelete: boolean
  hasPrivilegeToView: boolean
  noResults: boolean,
  beforeLogout: boolean,
  lastUpdatedAt: Date | undefined,
  columnDefs: (ColGroupDef | ColDef)[],
  autoGroupColumnDef: ColDef,
  suppressAggFuncInHeader: boolean,
  groupSelectsChildren: boolean,
  suppressRowClickSelection: boolean,
  enableGroupSelection: boolean,
  accessFunctionsColumnDefsVersion: string,
  onRefresh: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void,
  onCreateNew: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void,
  onGetGridSettings: (() => any) | undefined,
  onSelectionChanged: (event: SelectionChangedEvent) => void,
  onSaveGridSettings: (gridSettings: GridSettings) => void,
  onSelectItemMenuAction: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void,
  onShowActionMenuItem: (type: AccessFunctionMenuActions, accessFunctionTag: string) => void,
  handleExport?: (afIds: string[], headers: string[], gridRowData: RowNode[], exportOption: ExportOption) => void,
}

const AccessFunctionGrid: FunctionComponent<Props> = ({
  hasPrivilegesToCreate,
  hasPrivilegeToDelete,
  hasPrivilegeToView,
  data = [],
  noResults,
  beforeLogout,
  lastUpdatedAt,
  columnDefs,
  autoGroupColumnDef,
  suppressAggFuncInHeader,
  groupSelectsChildren,
  suppressRowClickSelection,
  enableGroupSelection,
  accessFunctionsColumnDefsVersion,
  onCreateNew,
  onRefresh,
  onSelectionChanged,
  onSaveGridSettings,
  onGetGridSettings,
  onShowActionMenuItem,
  onSelectItemMenuAction,
  handleExport,
}: Props) => {
  const commonGridRef = useRef<CommonGrid>(null);
  const exportReportDialogBodyRef = useRef<ExportReportDialogBodyFormRef>(null);
  const [exportReportDialog, setExportReportDialog] = useConfirmActionDialog();

  const frameworkComponents = {
    actionMenuCellRenderer: ActionMenuCellRenderer,
    notApplicableCellRenderer: NotApplicableCellRenderer,
    accessFunctionStatusCellRenderer: AccessFunctionStatusCellRenderer,
    accessFunctionDestinationCellRenderer: AccessFunctionDestinationCellRenderer,
    accessFunctionConfiguredStatusCellRenderer: AccessFunctionConfiguredStatusCellRenderer,
  };

  const { t } = useTranslation();

  const onHandleExport = () => {
    setExportReportDialog(
      t('accessFunction.exportPopup.title'),
      <ExportReportDialogBody
        ref={exportReportDialogBodyRef}
        exportOption={{
          supportedFileFormats: ['json', 'csv'],
          fileFormat: 'json' as 'json' | 'csv' | 'xml' | 'pdf' | 'txt',
          filename: `afs_export_${moment().valueOf()}`,
          isExporting: false
        }}
      >
        {t('accessFunction.exportPopup.message')}
      </ExportReportDialogBody>,
      t('accessFunction.exportPopup.action'),
      onSubmitCallback
    );
  };

  const onSubmitCallback = () => {
    const afIds: string[] = [];
    const rowData: RowNode[] = [];
    let headers: string[] = [];
    (commonGridRef?.current?.gridApi as GridApi).forEachNodeAfterFilterAndSort((node: RowNode) => {
      rowData.push(node);
      if (node.aggData) {
        afIds.push(node.aggData.afid);
      }
    });

    (commonGridRef?.current?.gridColumnApi as ColumnApi).getAllGridColumns().forEach((column, index) => {
      headers.push((commonGridRef?.current?.gridColumnApi as ColumnApi).getDisplayNameForColumn(column, 'header'));
    });

    if (headers && headers.length > 0) {
      headers = headers.splice(2);
    }

    if (handleExport) {
      handleExport(afIds, headers, rowData, exportReportDialogBodyRef.current?.getExportOption() as ExportOption);
    }
  };

  return (
    <div
      className="grid-container grid-group-header-container access-function-list"
      style={{ height: 'inherit' }}
    >
      <CommonGrid
        ref={commonGridRef}
        createNewText={t('accessFunction.grid.createNew')}
        pagination
        enableBrowserTooltips
        rowSelection={ROWSELECTION.MULTIPLE}
        hasPrivilegeToCreateNew={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        hasPrivilegetoView={hasPrivilegeToView}
        rowData={data}
        noResults={noResults}
        isBeforeLogout={beforeLogout}
        columnDefs={columnDefs}
        columnDefsVersion={accessFunctionsColumnDefsVersion}
        autoGroupColumnDef={autoGroupColumnDef}
        components={frameworkComponents}
        actionMenuClass={AccessFunctionActionMenu}
        handleCreateNew={onCreateNew}
        onRefresh={onRefresh}
        onSelectionChanged={onSelectionChanged}
        showActionMenuItem={onShowActionMenuItem}
        onActionMenuItemClick={onSelectItemMenuAction}
        saveGridSettings={onSaveGridSettings}
        getGridSettingsFromServer={onGetGridSettings}
        lastUpdatedAt={lastUpdatedAt}
        suppressAggFuncInHeader={suppressAggFuncInHeader}
        groupSelectsChildren={groupSelectsChildren}
        suppressRowClickSelection={suppressRowClickSelection}
        enableGroupSelection={enableGroupSelection}
        onExport={onHandleExport}
      />
      <ModalDialog
        width="500px"
        isOpen={exportReportDialog.isOpen}
        title={exportReportDialog.title}
        customComponent={exportReportDialog.customComponent}
        actionText={exportReportDialog.actionText}
        onSubmit={exportReportDialog.onSubmit}
        onClose={exportReportDialog.onClose}
        doNotDisplayCancelButton={exportReportDialog.hideCancel}
      />
    </div>
  );
};

export default AccessFunctionGrid;
